import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Notification } from '../../types/api';
import { formatRelative } from '../../utils/date';
import { getNotificationStyle } from './notificationStyles';

export type NotificationFilter = 'all' | 'unread';

export interface NotificationDropdownProps {
  notifications: Notification[];
  isLoading: boolean;
  filter: NotificationFilter;
  onFilterChange: (filter: NotificationFilter) => void;
  onItemClick: (notification: Notification) => void;
  onMarkAllRead: () => void;
  viewAllPath: string;
  /** Triggered when the user clicks the "View all" link. Useful to close the dropdown. */
  onViewAll?: () => void;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  notifications,
  isLoading,
  filter,
  onFilterChange,
  onItemClick,
  onMarkAllRead,
  viewAllPath,
  onViewAll,
}) => {
  const navigate = useNavigate();

  const visible = filter === 'unread'
    ? notifications.filter((n) => !n.is_read)
    : notifications;
  const latest = visible.slice(0, 10);

  const handleViewAll = () => {
    onViewAll?.();
    navigate(viewAllPath);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="fixed sm:absolute top-14 sm:top-auto right-2 sm:right-0 mt-0 sm:mt-2 w-[calc(100vw-1rem)] max-w-sm sm:w-96 sm:max-w-none bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 z-50 overflow-hidden"
      data-testid="notification-dropdown"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Notifications
          </h3>
        </div>
        <div className="flex space-x-1">
          <FilterTab
            active={filter === 'all'}
            onClick={() => onFilterChange('all')}
            label="All"
          />
          <FilterTab
            active={filter === 'unread'}
            onClick={() => onFilterChange('unread')}
            label="Unread"
          />
        </div>
      </div>

      {/* Body */}
      <div className="max-h-96 overflow-y-auto">
        {isLoading ? (
          <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            Loading...
          </div>
        ) : latest.length === 0 ? (
          <div className="py-8 px-4 text-center text-sm text-gray-500 dark:text-gray-400">
            {filter === 'unread'
              ? 'No unread notifications'
              : 'No notifications yet'}
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {latest.map((n) => (
              <NotificationItem
                key={n.id}
                notification={n}
                onClick={() => onItemClick(n)}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
        <button
          onClick={handleViewAll}
          className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
        >
          View all
        </button>
        <button
          onClick={onMarkAllRead}
          className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
        >
          Mark all read
        </button>
      </div>
    </motion.div>
  );
};

interface FilterTabProps {
  active: boolean;
  onClick: () => void;
  label: string;
}

const FilterTab: React.FC<FilterTabProps> = ({ active, onClick, label }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
      active
        ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'
        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
    }`}
  >
    {label}
  </button>
);

interface NotificationItemProps {
  notification: Notification;
  onClick: () => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onClick,
}) => {
  const style = getNotificationStyle(notification.type, notification.icon);
  const unread = !notification.is_read;

  return (
    <li>
      <button
        onClick={onClick}
        className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors ${
          unread ? 'bg-blue-50 dark:bg-blue-900/20' : ''
        }`}
      >
        <div className="flex items-start space-x-3">
          {/* Icon box */}
          <div
            className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-base ${style.bg}`}
            aria-hidden="true"
          >
            <span>{style.icon}</span>
          </div>
          {/* Content */}
          <div className="flex-1 min-w-0">
            <p
              className={`text-sm ${
                unread
                  ? 'font-semibold text-gray-900 dark:text-gray-100'
                  : 'font-medium text-gray-800 dark:text-gray-200'
              } truncate`}
            >
              {notification.title}
            </p>
            {notification.message && (
              <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mt-0.5">
                {notification.message}
              </p>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
              {formatRelative(notification.created_date)}
            </p>
          </div>
          {/* Unread dot */}
          {unread && (
            <span
              className="flex-shrink-0 w-2 h-2 rounded-full bg-primary-500 mt-2"
              aria-label="Unread"
              data-testid="unread-dot"
            />
          )}
        </div>
      </button>
    </li>
  );
};

export default NotificationDropdown;
